import { createStore } from 'vuex'

export default createStore({
  state: {
    user: {
      isAuthenticated: false,
      profile: null
    },
    markets: {
      pairs: [],
      watchlist: [],
      loading: false,
      error: null
    },
    wallet: {
      balances: [],
      transactions: [],
      loading: false,
      error: null
    },
    trading: {
      currentPair: null,
      orderBook: {
        bids: [],
        asks: []
      },
      recentTrades: [],
      openOrders: [],
      loading: false,
      error: null
    }
  },
  getters: {
    isAuthenticated: state => state.user.isAuthenticated,
    userProfile: state => state.user.profile,
    totalBalance: state => {
      return state.wallet.balances.reduce((total, asset) => {
        return total + (asset.balance * asset.price)
      }, 0)
    },
    marketPairs: state => state.markets.pairs,
    userWatchlist: state => state.markets.watchlist,
    walletBalances: state => state.wallet.balances,
    walletTransactions: state => state.wallet.transactions,
    currentTradingPair: state => state.trading.currentPair,
    orderBook: state => state.trading.orderBook,
    recentTrades: state => state.trading.recentTrades,
    openOrders: state => state.trading.openOrders
  },
  mutations: {
    SET_AUTHENTICATED(state, status) {
      state.user.isAuthenticated = status
    },
    SET_USER_PROFILE(state, profile) {
      state.user.profile = profile
    },
    SET_MARKET_PAIRS(state, pairs) {
      state.markets.pairs = pairs
    },
    SET_WATCHLIST(state, watchlist) {
      state.markets.watchlist = watchlist
    },
    ADD_TO_WATCHLIST(state, pair) {
      state.markets.watchlist.push(pair)
    },
    REMOVE_FROM_WATCHLIST(state, pairSymbol) {
      state.markets.watchlist = state.markets.watchlist.filter(pair => pair.symbol !== pairSymbol)
    },
    SET_MARKETS_LOADING(state, status) {
      state.markets.loading = status
    },
    SET_MARKETS_ERROR(state, error) {
      state.markets.error = error
    },
    SET_WALLET_BALANCES(state, balances) {
      state.wallet.balances = balances
    },
    SET_WALLET_TRANSACTIONS(state, transactions) {
      state.wallet.transactions = transactions
    },
    SET_WALLET_LOADING(state, status) {
      state.wallet.loading = status
    },
    SET_WALLET_ERROR(state, error) {
      state.wallet.error = error
    },
    SET_CURRENT_TRADING_PAIR(state, pair) {
      state.trading.currentPair = pair
    },
    SET_ORDER_BOOK(state, orderBook) {
      state.trading.orderBook = orderBook
    },
    SET_RECENT_TRADES(state, trades) {
      state.trading.recentTrades = trades
    },
    SET_OPEN_ORDERS(state, orders) {
      state.trading.openOrders = orders
    },
    ADD_OPEN_ORDER(state, order) {
      state.trading.openOrders.unshift(order)
    },
    REMOVE_OPEN_ORDER(state, orderId) {
      state.trading.openOrders = state.trading.openOrders.filter(order => order.id !== orderId)
    },
    SET_TRADING_LOADING(state, status) {
      state.trading.loading = status
    },
    SET_TRADING_ERROR(state, error) {
      state.trading.error = error
    }
  },
  actions: {
    // Auth actions
    login({ commit }, credentials) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          // Simulate successful login
          localStorage.setItem('auth_token', 'demo_token')
          commit('SET_AUTHENTICATED', true)
          
          // Simulate fetching user profile
          const userProfile = {
            id: 1,
            name: 'John Doe',
            email: credentials.email,
            joinDate: 'January 2023'
          }
          commit('SET_USER_PROFILE', userProfile)
          resolve(userProfile)
        }, 1000)
      })
    },
    
    logout({ commit }) {
      // Clear auth token
      localStorage.removeItem('auth_token')
      commit('SET_AUTHENTICATED', false)
      commit('SET_USER_PROFILE', null)
    },
    
    checkAuth({ commit }) {
      // Check if user is authenticated based on local storage
      const token = localStorage.getItem('auth_token')
      if (token) {
        commit('SET_AUTHENTICATED', true)
        // Here you would also fetch the user profile
      } else {
        commit('SET_AUTHENTICATED', false)
      }
    },
    
    // Market actions
    fetchMarketPairs({ commit }) {
      commit('SET_MARKETS_LOADING', true)
      
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          const pairs = [
            { symbol: 'BTC/USDT', name: 'Bitcoin', price: 35000, priceChange: 2.4, volume: 28000000000 },
            { symbol: 'ETH/USDT', name: 'Ethereum', price: 2000, priceChange: 3.8, volume: 12000000000 },
            { symbol: 'SOL/USDT', name: 'Solana', price: 80, priceChange: 5.2, volume: 3500000000 },
            { symbol: 'LINK/USDT', name: 'Chainlink', price: 15, priceChange: -1.2, volume: 950000000 },
            { symbol: 'ADA/USDT', name: 'Cardano', price: 0.45, priceChange: -0.8, volume: 750000000 }
          ]
          
          commit('SET_MARKET_PAIRS', pairs)
          commit('SET_MARKETS_LOADING', false)
          resolve(pairs)
        }, 1000)
      })
    },
    
    fetchWatchlist({ commit }) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          const watchlist = [
            { symbol: 'BTC/USDT', name: 'Bitcoin', price: 35000, priceChange: 2.4, volume: 28000000000 },
            { symbol: 'ETH/USDT', name: 'Ethereum', price: 2000, priceChange: 3.8, volume: 12000000000 },
            { symbol: 'SOL/USDT', name: 'Solana', price: 80, priceChange: 5.2, volume: 3500000000 }
          ]
          
          commit('SET_WATCHLIST', watchlist)
          resolve(watchlist)
        }, 500)
      })
    },
    
    // Wallet actions
    fetchWalletBalances({ commit }) {
      commit('SET_WALLET_LOADING', true)
      
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          const balances = [
            { symbol: 'BTC', name: 'Bitcoin', balance: 0.5, price: 35000, priceChange: 2.4 },
            { symbol: 'ETH', name: 'Ethereum', balance: 2.5, price: 2000, priceChange: 3.8 },
            { symbol: 'SOL', name: 'Solana', balance: 15, price: 80, priceChange: 5.2 },
            { symbol: 'USDT', name: 'Tether', balance: 113.89, price: 1, priceChange: 0 }
          ]
          
          commit('SET_WALLET_BALANCES', balances)
          commit('SET_WALLET_LOADING', false)
          resolve(balances)
        }, 1000)
      })
    },
    
    fetchWalletTransactions({ commit }) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          const transactions = [
            { id: 1, type: 'deposit', asset: 'BTC', amount: 0.1, status: 'completed', time: '2 hours ago' },
            { id: 2, type: 'buy', asset: 'ETH', amount: 1.5, price: 1950, status: 'completed', time: '1 day ago' },
            { id: 3, type: 'sell', asset: 'SOL', amount: 10, price: 75, status: 'completed', time: '3 days ago' },
            { id: 4, type: 'withdraw', asset: 'BTC', amount: 0.05, status: 'pending', time: '1 hour ago' }
          ]
          
          commit('SET_WALLET_TRANSACTIONS', transactions)
          resolve(transactions)
        }, 500)
      })
    },
    
    // Trading actions
    fetchTradingData({ commit }, symbol) {
      commit('SET_TRADING_LOADING', true)
      
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          // Set current pair
          const currentPair = {
            symbol: symbol || 'BTC/USDT',
            baseCurrency: symbol ? symbol.split('/')[0] : 'BTC',
            quoteCurrency: symbol ? symbol.split('/')[1] : 'USDT',
            price: 35000,
            priceChange: 2.4,
            volume: 28000000000
          }
          commit('SET_CURRENT_TRADING_PAIR', currentPair)
          
          // Set order book
          const orderBook = {
            asks: [
              { price: 35100, amount: 0.42 },
              { price: 35050, amount: 0.85 },
              { price: 35025, amount: 1.23 },
              { price: 35010, amount: 0.56 },
              { price: 35005, amount: 2.11 },
              { price: 35001, amount: 0.98 }
            ],
            bids: [
              { price: 34999, amount: 1.45 },
              { price: 34990, amount: 0.74 },
              { price: 34975, amount: 1.89 },
              { price: 34950, amount: 0.63 },
              { price: 34925, amount: 2.35 },
              { price: 34900, amount: 1.05 }
            ]
          }
          commit('SET_ORDER_BOOK', orderBook)
          
          // Set recent trades
          const recentTrades = [
            { price: 35001, amount: 0.12, side: 'buy', time: '12:45:22' },
            { price: 34999, amount: 0.08, side: 'sell', time: '12:45:18' },
            { price: 35005, amount: 0.25, side: 'buy', time: '12:44:55' },
            { price: 34980, amount: 0.5, side: 'sell', time: '12:44:30' }
          ]
          commit('SET_RECENT_TRADES', recentTrades)
          
          commit('SET_TRADING_LOADING', false)
          resolve({ currentPair, orderBook, recentTrades })
        }, 1000)
      })
    },
    
    fetchOpenOrders({ commit }) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          const openOrders = [
            { 
              id: 1, 
              date: '2023-09-15 12:30', 
              pair: 'BTC/USDT', 
              type: 'Limit', 
              side: 'buy', 
              price: 34800, 
              amount: 0.5, 
              filled: 0, 
              total: 17400 
            },
            { 
              id: 2, 
              date: '2023-09-15 11:45', 
              pair: 'ETH/USDT', 
              type: 'Limit', 
              side: 'sell', 
              price: 2050, 
              amount: 2, 
              filled: 50, 
              total: 4100 
            }
          ]
          
          commit('SET_OPEN_ORDERS', openOrders)
          resolve(openOrders)
        }, 500)
      })
    },
    
    placeOrder({ commit }, orderData) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          // Create a new order with an ID
          const newOrder = {
            id: Date.now(),
            date: new Date().toLocaleString(),
            ...orderData,
            filled: 0
          }
          
          commit('ADD_OPEN_ORDER', newOrder)
          resolve(newOrder)
        }, 1000)
      })
    },
    
    cancelOrder({ commit }, orderId) {
      // This would be an API call in a real app
      return new Promise((resolve) => {
        setTimeout(() => {
          commit('REMOVE_OPEN_ORDER', orderId)
          resolve({ success: true, orderId })
        }, 500)
      })
    }
  },
  modules: {
  }
})
